import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Flex,
  ScaleFade,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useMemo, useRef, useState } from 'react';
import { FiKey, FiUserPlus } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

import { CopySource } from '../../../../components/_components_0.2/AccessControl/ItemUsersEditor';
import { HeaderUp } from '../../../../components/_components_0.2/Header_0.2';
import { ModalAccessItem } from '../../../../components/_components_0.2/Modal/ModalAccessItem';
import { ModalItemUsers } from '../../../../components/_components_0.2/Modal/ModalItemUsers';
import { Sidebar } from '../../../../components/_components_0.2/Sidebar_0.2';
import { apllyToast } from '../../../../components/Global/Toast2.0';
import {
  ACCESS_CONTROL_KEY,
  useAccessControlItems,
  useAccessControlUsers,
} from '../../../../hooks/useAccessControl';
import {
  AccessControlItem,
  AccessControlKind,
  accessControlError,
  accessControlService,
} from '../../../../services/accessControlService';
import { KIND_LABEL, plural } from '../../../../utils/accessControl';
import { AccessItemsTab } from './AccessItemsTab';
import { CardButton, CardeHeader, Container } from './styles';

// Mesmos códigos protegidos da API.
const PROTECTED: Record<AccessControlKind, string[]> = {
  permission: ['user_adm'],
  role: ['master_user'],
};

interface Target {
  kind: AccessControlKind;
  item?: AccessControlItem;
}

export function PermissionsAndRolesManager() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const roles = useAccessControlItems('role');
  const permissions = useAccessControlItems('permission');
  const users = useAccessControlUsers();

  const itemModal = useDisclosure();
  const usersModal = useDisclosure();
  const [target, setTarget] = useState<Target>({ kind: 'permission' });
  const [toDelete, setToDelete] = useState<Target | null>(null);
  const [deleting, setDeleting] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const itemsOf = (kind: AccessControlKind) =>
    (kind === 'permission' ? permissions.data : roles.data) || [];

  const copySources: CopySource[] = useMemo(
    () => [
      ...(permissions.data || [])
        .filter(item => item.total_accesses > 0)
        .map(item => ({ kind: 'permission' as const, item })),
      ...(roles.data || [])
        .filter(item => item.total_accesses > 0)
        .map(item => ({ kind: 'role' as const, item })),
    ],
    [permissions.data, roles.data],
  );

  const openItem = (kind: AccessControlKind, item?: AccessControlItem) => {
    setTarget({ kind, item });
    itemModal.onOpen();
  };

  const openUsers = (kind: AccessControlKind, item: AccessControlItem) => {
    setTarget({ kind, item });
    usersModal.onOpen();
  };

  const deleteBlock = (() => {
    if (!toDelete?.item) {
      return undefined;
    }

    const { kind, item } = toDelete;

    if (PROTECTED[kind].includes(item.identification.trim().toLowerCase())) {
      return `"${item.name}" é usada diretamente pelo sistema e não pode ser excluída.`;
    }

    if (kind === 'permission' && item.bi_reports > 0) {
      return `Esta permissão libera ${plural(
        item.bi_reports,
        'relatório',
        'relatórios',
      )} de BI. Troque a permissão desses relatórios em Administração › Business Intelligence antes de excluir; senão eles poderiam ficar abertos para todos.`;
    }

    return undefined;
  })();

  const confirmDelete = async () => {
    if (!toDelete?.item) {
      return;
    }

    setDeleting(true);
    try {
      await accessControlService.deleteItem(toDelete.kind, toDelete.item.id);
      await queryClient.invalidateQueries(ACCESS_CONTROL_KEY);
      apllyToast('success', `${KIND_LABEL[toDelete.kind].title} excluída.`);
      setToDelete(null);
    } catch (err) {
      apllyToast(
        'error',
        accessControlError(
          err,
          `Não foi possível excluir a ${KIND_LABEL[toDelete.kind].one}.`,
        ),
      );
    } finally {
      setDeleting(false);
    }
  };

  const tabs = [
    { kind: 'role' as const, query: roles },
    { kind: 'permission' as const, query: permissions },
  ];

  return (
    <>
      <HeaderUp path="adm" />

      <Flex w="100%" maxWidth={1480} mx="auto" pb={4} px="6">
        <Sidebar path="adm" />

        <Box flex="1" minW="0">
          <ScaleFade initialScale={0.9} in>
            <Container>
              <CardeHeader>
                <div>
                  <h2>Funções e Permissões</h2>
                  <strong>
                    Gerencie os acessos da aplicação: crie, edite e atribua aos
                    usuários
                  </strong>
                </div>

                <CardButton>
                  <Button
                    leftIcon={<FiUserPlus />}
                    onClick={() => openItem('role')}
                    type="button"
                    colorScheme="blue"
                    color="white"
                  >
                    Adicionar nova função
                  </Button>
                  <Button
                    leftIcon={<FiKey />}
                    onClick={() => openItem('permission')}
                    type="button"
                    colorScheme="blue"
                    color="white"
                  >
                    Adicionar nova permissão
                  </Button>
                </CardButton>
              </CardeHeader>

              <Tabs variant="enclosed" index={tab} onChange={setTab} isLazy>
                <TabList>
                  {tabs.map(({ kind, query }) => (
                    <Tab key={kind}>
                      {KIND_LABEL[kind].titleMany}
                      {query.data ? ` (${query.data.length})` : ''}
                    </Tab>
                  ))}
                </TabList>
                <TabPanels>
                  {tabs.map(({ kind, query }) => (
                    <TabPanel key={kind} px="0">
                      <AccessItemsTab
                        kind={kind}
                        query={query}
                        onUsers={item => openUsers(kind, item)}
                        onEdit={item => openItem(kind, item)}
                        onDelete={item => setToDelete({ kind, item })}
                      />
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </Container>
          </ScaleFade>
        </Box>
      </Flex>

      <ModalAccessItem
        kind={target.kind}
        item={target.item}
        isOpen={itemModal.isOpen}
        onClose={itemModal.onClose}
        items={itemsOf(target.kind)}
        users={users.data || []}
        usersLoading={users.isLoading}
        copySources={copySources}
      />

      <ModalItemUsers
        kind={target.kind}
        item={target.item}
        isOpen={usersModal.isOpen}
        onClose={usersModal.onClose}
        users={users.data || []}
        usersLoading={users.isLoading}
        copySources={copySources}
      />

      <AlertDialog
        isOpen={!!toDelete}
        leastDestructiveRef={cancelRef}
        onClose={() => !deleting && setToDelete(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.700">
            <AlertDialogHeader>
              Excluir {toDelete ? KIND_LABEL[toDelete.kind].one : ''}
            </AlertDialogHeader>
            <AlertDialogBody>
              {toDelete?.item && (
                <>
                  <Text>
                    Excluir <b>{toDelete.item.name}</b> (
                    <Text as="span" fontFamily="mono" fontSize="sm">
                      {toDelete.item.identification}
                    </Text>
                    )?
                  </Text>
                  {deleteBlock ? (
                    <Alert
                      status="error"
                      color="red.900"
                      borderRadius="md"
                      mt="4"
                    >
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        {deleteBlock}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert
                      status="warning"
                      color="orange.900"
                      borderRadius="md"
                      mt="4"
                    >
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        {toDelete.item.total_accesses > 0
                          ? `${plural(
                              toDelete.item.total_accesses,
                              'usuário perde',
                              'usuários perdem',
                            )} esta ${KIND_LABEL[toDelete.kind].one}. `
                          : ''}
                        Não dá para desfazer.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant="ghost"
                onClick={() => setToDelete(null)}
                isDisabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={confirmDelete}
                isLoading={deleting}
                isDisabled={!!deleteBlock}
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
